import { useEffect, useMemo, useState, type ChangeEvent } from "react";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Heading,
  IconButton,
  Image,
  Input,
  Link as ChakraLink,
  Select,
  SimpleGrid,
  Spinner,
  Stack,
  Switch,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { ArrowBackIcon, ExternalLinkIcon } from "@chakra-ui/icons";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import {
  CertificationsApi,
  WorksApi,
  uploadWorkImage,
  type WorkCertification,
  type WorkDetail as WorkDetailT,
  type WorkPatch,
} from "../api/works";
import { statusColor } from "../utils/certStatus";
import { fmtDate } from "../utils/datetime";
import logger from "../analytics/logger";
import ReasonField from "../components/reason/ReasonField";
import SourceFileField from "../components/SourceFileField";
import { buildProvenance } from "../provenance/buildProvenance";

function clone<T>(v: T): T {
  return typeof structuredClone === "function"
    ? structuredClone(v)
    : (JSON.parse(JSON.stringify(v)) as T);
}

function diff(orig: WorkDetailT, draft: WorkDetailT): WorkPatch {
  const patch: WorkPatch = {};
  if (orig.title !== draft.title) patch.title = draft.title;
  if (orig.type !== draft.type) patch.type = draft.type;
  if (orig.format !== draft.format) patch.format = draft.format;
  if (orig.description !== draft.description) patch.description = draft.description;
  if (orig.url !== draft.url) patch.url = draft.url;
  if (orig.img_asset !== draft.img_asset) patch.img_asset = draft.img_asset;
  return patch;
}

function prettyEvidence(raw: string): { text: string; isJson: boolean } {
  const trimmed = raw.trim();
  if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
    try {
      return { text: JSON.stringify(JSON.parse(trimmed), null, 2), isJson: true };
    } catch {
      // not valid JSON — fall through
    }
  }
  return { text: raw, isJson: false };
}

function s3ToConsoleUrl(url: string): { href: string; label: string } | null {
  const m = url.match(/^s3:\/\/([^/]+)\/(.+)$/);
  if (!m) return null;
  const [, bucket, key] = m;
  const region = "us-west-2";
  const href = `https://${region}.console.aws.amazon.com/s3/object/${bucket}?region=${region}&prefix=${encodeURIComponent(key)}`;
  return { href, label: "S3 Object" };
}

// API contract is UPPER (backend serializers.status_name). The
// /manual-override POST body keys are lowercase per backend
// _TARGET_STATUSES; we lowercase at the call site below.
const CERT_STATUSES = ["PENDING", "APPROVED", "REJECTED"] as const;

function deriveOverrideStatus(supportingData: string | null): string | null {
  if (!supportingData) return null;
  const head = supportingData.trimStart().toLowerCase();
  if (head.startsWith("manual approval:")) return "APPROVED";
  if (head.startsWith("manual rejection:")) return "REJECTED";
  return null;
}

function EvidenceRow({
  ev,
  showHistoricalStatus,
}: {
  ev: WorkCertification["evidence"][number];
  showHistoricalStatus: boolean;
}) {
  const pretty = ev.supporting_data ? prettyEvidence(ev.supporting_data) : null;
  // Default-collapse JSON payloads (often large); leave plain-text expanded.
  const [open, setOpen] = useState(!pretty?.isJson);
  const historicalStatus = showHistoricalStatus
    ? deriveOverrideStatus(ev.supporting_data)
    : null;

  return (
    <Box fontSize="xs" color="gray.700">
      <HStack spacing={2} wrap="wrap">
        <IconButton
          aria-label={open ? "Collapse" : "Expand"}
          size="xs"
          variant="ghost"
          minW="20px"
          h="20px"
          icon={
            <Box as="span" fontSize="10px">
              {open ? "▼" : "▶"}
            </Box>
          }
          onClick={() => setOpen((v) => !v)}
        />
        <Badge variant="subtle" textTransform="none">
          {ev.type_name ?? "(no type)"}
        </Badge>
        {historicalStatus && (
          <Badge colorScheme={statusColor(historicalStatus)} textTransform="none">
            {historicalStatus}
          </Badge>
        )}
        {ev.approved_by && <Text color="gray.600">by {ev.approved_by}</Text>}
        {ev.approved_at && <Text color="gray.500">on {fmtDate(ev.approved_at)}</Text>}
      </HStack>
      {open && pretty && (
        <Text
          mt={1}
          ml={6}
          whiteSpace="pre-wrap"
          wordBreak="break-word"
          fontFamily={pretty.isJson ? "mono" : undefined}
          fontSize={pretty.isJson ? "11px" : undefined}
        >
          {pretty.text}
        </Text>
      )}
      {open && ev.comment && (
        <Text mt={0.5} ml={6} fontStyle="italic" color="gray.500">
          {ev.comment}
        </Text>
      )}
    </Box>
  );
}

function CertCard({
  cert,
  editing,
  onApproved,
  workType,
}: {
  cert: WorkCertification;
  editing: boolean;
  onApproved: () => void;
  workType: string;
}) {
  const [proposed, setProposed] = useState<string>(cert.status ?? "PENDING");
  const [reason, setReason] = useState("");
  const [sourceFile, setSourceFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [regenSubmitting, setRegenSubmitting] = useState(false);
  const [regenSubmitted, setRegenSubmitted] = useState(false);

  const regenerate = async () => {
    setRegenSubmitting(true);
    setError(null);
    try {
      await CertificationsApi.regenerateSignature(cert.id);
      setRegenSubmitted(true);
    } catch (e) {
      logger.error("regenerate_signature_failed", e);
      const err = e as { response?: { data?: { error?: string } } };
      setError(err?.response?.data?.error ?? "Failed to regenerate");
    } finally {
      setRegenSubmitting(false);
    }
  };

  const isOverride =
    editing &&
    (proposed === "APPROVED" || proposed === "REJECTED") &&
    proposed !== cert.status &&
    !submitted;
  const isApproveFlow = proposed === "APPROVED";

  const submit = async () => {
    if (!reason.trim()) {
      setError("Reason is required");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const provenance =
        isApproveFlow && sourceFile ? ((await buildProvenance(sourceFile)) ?? undefined) : undefined;
      await CertificationsApi.manualOverride(
        cert.id,
        proposed.toLowerCase() as "approved" | "rejected",
        reason.trim(),
        provenance,
      );
      setSubmitted(true);
      onApproved();
    } catch (e) {
      logger.error("manual_override_failed", e);
      const err = e as { response?: { data?: { error?: string } } };
      setError(err?.response?.data?.error ?? "Failed to save");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box p={3} borderWidth="1px" borderRadius="md" bg="white">
      <Flex align="center" wrap="wrap" gap={2}>
        {editing ? (
          <Select
            size="xs"
            maxW="140px"
            value={proposed}
            onChange={(e) => setProposed(e.target.value)}
          >
            {CERT_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </Select>
        ) : (
          <Badge colorScheme={statusColor(cert.status)} textTransform="none">
            {cert.status ?? "—"}
          </Badge>
        )}
        <Text fontSize="sm" fontWeight="medium">
          {cert.type ? `${cert.type.name} v${cert.type.version}` : "(no type)"}
        </Text>
        <Box flex="1" />
        <Text fontSize="xs" color="gray.500">
          {fmtDate(cert.created_at)}
        </Text>
      </Flex>
      {(cert.approved_by || cert.approved_at) && (
        <Text fontSize="xs" color="gray.600" mt={1}>
          Approved {cert.approved_by ? `by ${cert.approved_by}` : ""}
          {cert.approved_at ? ` on ${fmtDate(cert.approved_at)}` : ""}
        </Text>
      )}
      {cert.status === "APPROVED" && (
        <HStack spacing={2} mt={2}>
          <Button
            size="xs"
            variant="outline"
            onClick={regenerate}
            isLoading={regenSubmitting}
            isDisabled={regenSubmitted}
          >
            {regenSubmitted ? "Regeneration queued" : "Regenerate signature"}
          </Button>
          {regenSubmitted && (
            <Text fontSize="xs" color="gray.500">
              Signature will refresh within ~1 minute. Reload to see.
            </Text>
          )}
        </HStack>
      )}
      {cert.cert_signature_b64 && (
        <Text
          fontSize="xs"
          color="gray.500"
          fontFamily="mono"
          mt={1}
          wordBreak="break-all"
        >
          cert_signature_b64: {cert.cert_signature_b64}
        </Text>
      )}
      {cert.external_id && (
        <Text fontSize="xs" color="gray.500" fontFamily="mono" mt={1}>
          external_id: {cert.external_id}
        </Text>
      )}
      {cert.evidence.length > 0 && (() => {
        // Evidence is desc by created_at; the first row whose supporting_data
        // encodes an override is the "most recent" status change — its
        // effect == cert.status displayed at the top of the card, so we hide
        // the historical badge there to avoid double-display.
        const topMostOverrideIdx = cert.evidence.findIndex((e) =>
          deriveOverrideStatus(e.supporting_data) !== null,
        );
        return (
          <Stack spacing={1.5} mt={3} pt={3} borderTopWidth="1px">
            <Text
              fontSize="xs"
              fontWeight="semibold"
              color="gray.500"
              textTransform="uppercase"
              letterSpacing="wider"
            >
              Evidence ({cert.evidence.length})
            </Text>
            {cert.evidence.map((ev, i) => (
              <EvidenceRow
                key={ev.id}
                ev={ev}
                showHistoricalStatus={i !== topMostOverrideIdx}
              />
            ))}
          </Stack>
        );
      })()}
      {isOverride && (
        <Stack spacing={2} mt={3} pt={3} borderTopWidth="1px">
          <Text fontSize="xs" fontWeight="semibold" color="gray.600" textTransform="uppercase" letterSpacing="wider">
            {isApproveFlow ? "Approval Reason" : "Rejection Reason"}
          </Text>
          <Textarea
            size="sm"
            rows={3}
            placeholder={
              isApproveFlow
                ? "External proof of human creation, etc."
                : "Why this certification is being rejected."
            }
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          {error && (
            <Alert status="error" borderRadius="md" fontSize="sm">
              <AlertIcon />
              {error}
            </Alert>
          )}
          {isApproveFlow && (
            <SourceFileField
              value={sourceFile}
              onChange={setSourceFile}
              workType={workType}
              isDisabled={submitting}
            />
          )}
          <Button
            size="sm"
            colorScheme={isApproveFlow ? "blue" : "red"}
            alignSelf="flex-start"
            isLoading={submitting}
            onClick={submit}
          >
            {isApproveFlow ? "Generate signed certification" : "Reject certification"}
          </Button>
        </Stack>
      )}
      {submitted && (
        <Alert status="success" mt={3} borderRadius="md" fontSize="sm">
          <AlertIcon />
          {isApproveFlow
            ? "Approval submitted. Signature will appear within ~1 minute."
            : "Rejection saved."}
        </Alert>
      )}
    </Box>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Box>
      <Text
        fontWeight="semibold"
        fontSize="xs"
        textTransform="uppercase"
        letterSpacing="wider"
        color="gray.500"
        mb={1.5}
      >
        {title}
      </Text>
      <Box fontSize="sm">{children}</Box>
    </Box>
  );
}

function Empty() {
  return <Text color="gray.400">—</Text>;
}

export default function WorkDetail() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [data, setData] = useState<WorkDetailT | null>(null);
  const [draft, setDraft] = useState<WorkDetailT | null>(null);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  // Local object-URL preview for a just-uploaded image (the presigned GET in
  // img_asset_url only refreshes after save/reload).
  const [newImagePreview, setNewImagePreview] = useState<string | null>(null);
  const [imgUploading, setImgUploading] = useState(false);

  const reload = () => {
    setLoading(true);
    setError(null);
    WorksApi.get(id)
      .then((d) => {
        setData(d);
        setDraft(null);
        setEditing(false);
        setNewImagePreview(null);
      })
      .catch((e) => {
        logger.error("work_get_failed", e);
        setError(e?.response?.data?.error ?? "Failed to load work");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    void (async () => {
      await reload();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const patch = useMemo(() => (data && draft ? diff(data, draft) : {}), [data, draft]);
  const dirty = Object.keys(patch).length > 0;

  const startEdit = () => {
    if (!data) return;
    setDraft(clone(data));
    setReason("");
    setNewImagePreview(null);
    setEditing(true);
  };

  const cancelEdit = () => {
    setDraft(null);
    setReason("");
    setNewImagePreview(null);
    setEditing(false);
    setError(null);
  };

  const handleImagePick = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImgUploading(true);
    setError(null);
    try {
      const key = await uploadWorkImage(file);
      setDraft((d) => (d ? { ...d, img_asset: key } : d));
      setNewImagePreview(URL.createObjectURL(file));
    } catch (err) {
      logger.error("work_image_upload_failed", err);
      setError((err as { message?: string })?.message ?? "Image upload failed");
    } finally {
      setImgUploading(false);
    }
  };

  const handleImageRemove = () => {
    setDraft((d) => (d ? { ...d, img_asset: null } : d));
    setNewImagePreview(null);
  };

  const save = async () => {
    if (!data || !draft) return;
    if (!dirty) {
      setEditing(false);
      setDraft(null);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const updated = await WorksApi.update(data.id, patch, { reason: reason.trim() });
      setData(updated);
      setDraft(null);
      setReason("");
      setEditing(false);
    } catch (e) {
      logger.error("work_update_failed", e);
      const err = e as { response?: { data?: { error?: string; field?: string } } };
      const code = err?.response?.data?.error ?? "Failed to save";
      const field = err?.response?.data?.field;
      setError(field ? `${code} (${field})` : code);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box py={10} textAlign="center">
        <Spinner />
      </Box>
    );
  }
  if (error && !data) {
    return (
      <Box p={6}>
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
      </Box>
    );
  }
  if (!data) return null;

  const view = editing && draft ? draft : data;
  const setField = <K extends keyof WorkDetailT>(k: K, v: WorkDetailT[K]) => {
    setDraft((d) => (d ? { ...d, [k]: v } : d));
  };

  return (
    <Box p={{ base: 4, md: 6 }} maxW="1100px" mx="auto">
      <Flex align="center" mb={4}>
        <IconButton
          aria-label="Back"
          icon={<ArrowBackIcon />}
          variant="ghost"
          size="sm"
          onClick={() => {
            cancelEdit();
            navigate(-1);
          }}
          mr={2}
        />
        <Text fontSize="sm" color="gray.500" flex="1">
          Work
        </Text>
        {editing ? (
          <HStack>
            <Button size="sm" onClick={cancelEdit} variant="ghost" isDisabled={saving}>
              Cancel
            </Button>
            <Button
              size="sm"
              colorScheme="blue"
              onClick={save}
              isLoading={saving}
              isDisabled={!dirty || !reason.trim()}
            >
              Save
            </Button>
          </HStack>
        ) : (
          <HStack spacing={2}>
            <Text fontSize="sm" color="gray.600">
              Edit
            </Text>
            <Switch size="sm" isChecked={editing} onChange={startEdit} />
          </HStack>
        )}
      </Flex>

      {error && (
        <Alert status="error" mb={4} borderRadius="md">
          <AlertIcon />
          {error}
        </Alert>
      )}

      {/* Header card */}
      <Box p={5} borderWidth="1px" borderRadius="lg" bg="white" mb={4}>
        {editing && draft ? (
          <Stack spacing={2}>
            <Input
              size="md"
              fontWeight="semibold"
              value={draft.title}
              placeholder="Title"
              onChange={(e) => setField("title", e.target.value)}
            />
            <HStack spacing={3} wrap="wrap">
              <Input
                size="sm"
                maxW="200px"
                value={draft.type}
                placeholder="Type"
                onChange={(e) => setField("type", e.target.value)}
              />
              <Input
                size="sm"
                maxW="200px"
                value={draft.format}
                placeholder="Format"
                onChange={(e) => setField("format", e.target.value)}
              />
            </HStack>
            <Input
              size="sm"
              value={draft.url ?? ""}
              placeholder="URL"
              onChange={(e) => setField("url", e.target.value || null)}
            />
          </Stack>
        ) : (
          <Stack spacing={2}>
            <Heading size="lg" wordBreak="break-word">
              {view.title || "(untitled)"}
            </Heading>
            <HStack spacing={2} wrap="wrap" pt={1}>
              <Badge colorScheme="purple" textTransform="none">
                {view.type}
              </Badge>
              <Badge variant="outline" textTransform="none">
                {view.format}
              </Badge>
              {view.url &&
                (() => {
                  const s3 = s3ToConsoleUrl(view.url);
                  const href = s3?.href ?? view.url;
                  const label = s3?.label ?? view.url;
                  return (
                    <ChakraLink
                      href={href}
                      isExternal
                      fontSize="sm"
                      color="blue.600"
                      wordBreak="break-all"
                    >
                      {label} <ExternalLinkIcon mx={1} />
                    </ChakraLink>
                  );
                })()}
            </HStack>
          </Stack>
        )}
      </Box>

      {/* Body grid */}
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5} mb={5}>
        <Section title="Created">{fmtDate(data.created_at)}</Section>
        <Section title="Updated">{fmtDate(data.updated_at)}</Section>

        <Section title="Creator">
          {data.creator ? (
            <Stack spacing={0.5}>
              <ChakraLink
                as={RouterLink}
                to={`/creators/${data.creator.id}`}
                color="blue.600"
                fontWeight="medium"
              >
                {data.creator.name ?? "(no name)"}
              </ChakraLink>
              <Text color="gray.600">{data.creator.email ?? "—"}</Text>
              <HStack spacing={2} pt={1}>
                <Badge colorScheme="purple" textTransform="none">
                  {data.creator.status}
                </Badge>
              </HStack>
            </Stack>
          ) : (
            <Empty />
          )}
        </Section>
        <Section title="Profile">
          {data.profile ? data.profile.name : <Empty />}
        </Section>

        <Section title="Work image">
          {(() => {
            const previewUrl =
              editing && draft
                ? newImagePreview ??
                  (draft.img_asset && draft.img_asset === data.img_asset
                    ? data.img_asset_url
                    : null)
                : data.img_asset_url;
            return (
              <Stack spacing={2} align="flex-start">
                {previewUrl ? (
                  <Image
                    src={previewUrl}
                    alt={data.title}
                    maxH="160px"
                    objectFit="contain"
                    borderRadius={6}
                  />
                ) : (
                  <Empty />
                )}
                {editing && draft && (
                  <HStack spacing={2}>
                    <Button as="label" size="xs" isDisabled={imgUploading} cursor="pointer">
                      {imgUploading ? <Spinner size="xs" /> : draft.img_asset ? "Replace…" : "Upload…"}
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={handleImagePick}
                      />
                    </Button>
                    {draft.img_asset && (
                      <Button
                        size="xs"
                        variant="ghost"
                        isDisabled={imgUploading}
                        onClick={handleImageRemove}
                      >
                        Remove
                      </Button>
                    )}
                  </HStack>
                )}
              </Stack>
            );
          })()}
        </Section>

        <Section title="Description">
          {editing && draft ? (
            <Textarea
              size="sm"
              rows={4}
              value={draft.description ?? ""}
              onChange={(e) => setField("description", e.target.value || null)}
            />
          ) : view.description ? (
            <Text whiteSpace="pre-wrap">{view.description}</Text>
          ) : (
            <Empty />
          )}
        </Section>
        <Section title="Work id">
          <Text fontFamily="mono" fontSize="xs" color="gray.600">
            {data.id}
          </Text>
        </Section>
      </SimpleGrid>

      {editing && draft && (
        <Box mb={5}>
          <ReasonField value={reason} onChange={setReason} />
        </Box>
      )}

      {/* Certifications */}
      <Box>
        <Text
          fontWeight="semibold"
          fontSize="xs"
          textTransform="uppercase"
          letterSpacing="wider"
          color="gray.500"
          mb={2}
        >
          Certifications ({data.certifications.length})
        </Text>
        {data.certifications.length === 0 ? (
          <Box p={4} borderWidth="1px" borderRadius="md" borderStyle="dashed">
            <Empty />
          </Box>
        ) : (
          <Stack spacing={2}>
            {data.certifications.map((c) => (
              <CertCard key={c.id} cert={c} editing={editing} onApproved={reload} workType={data.type} />
            ))}
          </Stack>
        )}
      </Box>
    </Box>
  );
}
